import { forwardRef, type ReactNode } from 'react'

import { Link as RouterLink } from 'react-router-dom'

import {
  isInternalLink,
  isResolveLink,
  resolveLink,
} from '@bettermode/common/url'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import type { LinkProps as LinkUiProps } from '@tribeplatform/react-ui-kit/Link'
import { Link as LinkUI } from '@tribeplatform/react-ui-kit/Link'

import { useResolveLink } from './useResolveLink.js'
import { getLinkRel, getLinkTarget, parseHref } from './utils/index.js'

export interface LinkProps extends Omit<LinkUiProps, 'rel'> {
  children: ReactNode
  trusted?: boolean
}

/**
 * Presentational link. Receives an already-resolved href so it never calls the
 * space-resolution hook — the hot path for the overwhelmingly non-token links.
 */
const LinkView = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, target, trusted, children, variant = 'inherit', ...props }, ref) => {
    const {
      data: { networkPublicInfo },
    } = useAuthToken()

    const parsedHref = parseHref(href, networkPublicInfo)
    const isInternal = isInternalLink(parsedHref)
    const linkTarget = getLinkTarget(target, isInternal)
    const isTrusted = trusted || isInternal
    const linkRel = getLinkRel(linkTarget, isTrusted)

    if (!href) {
      return (
        <LinkUI ref={ref} variant={variant} {...props}>
          {children}
        </LinkUI>
      )
    }

    if (isInternal) {
      return (
        <LinkUI
          ref={ref}
          as={RouterLink}
          to={parsedHref}
          variant={variant}
          {...(linkTarget ? { target: linkTarget } : {})}
          {...(linkRel ? { rel: linkRel } : {})}
          {...props}
        >
          {children}
        </LinkUI>
      )
    }

    return (
      <LinkUI
        ref={ref}
        href={parsedHref}
        variant={variant}
        {...(linkTarget ? { target: linkTarget } : {})}
        {...(linkRel ? { rel: linkRel } : {})}
        {...props}
      >
        {children}
      </LinkUI>
    )
  },
)

/**
 * Resolves a link token to its current path via `useResolveLink`, then renders
 * through `LinkView`. Only mounted when `resolveLink` actually parsed a token, so
 * plain links never pay for the resolution hook.
 */
const ResolvedLinkView = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, ...props }, ref) => {
    const resolvedHref = useResolveLink(href)
    return <LinkView ref={ref} href={resolvedHref} {...props} />
  },
)

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, ...props }: LinkProps, ref) => {
    const link = href ? resolveLink(href) : undefined
    if (isResolveLink(link)) {
      return <ResolvedLinkView ref={ref} href={href} {...props} />
    }

    return <LinkView ref={ref} href={href} {...props} />
  },
)
